import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * MonoValue — the brand's "numeric voice" (DESIGN Tabular Numerals rule). Use for every
 * ID, count, size, slug/key, IEC string, and timestamp. Applies `tnum`. Units get a thin
 * space (U+2009) before them.
 */
export function MonoValue({
  children,
  unit,
  className,
}: {
  children: ReactNode;
  unit?: string;
  className?: string;
}) {
  return (
    <span className={cn('tnum font-mono', className)}>
      {children}
      {unit ? `\u2009${unit}` : ''}
    </span>
  );
}

/** Mono uppercase eyebrow (DESIGN Mono-Eyebrow rule). Never sans-caps. */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('eyebrow text-accent-blue', className)}>{children}</span>;
}

/**
 * Almanac section header — a chapter-style mono eyebrow with an optional count, echoing the
 * public site's `§ NN / Name · Year` chapter marks (plan §2.5.2).
 */
export function PageHeader({
  eyebrow,
  title,
  count,
  actions,
}: {
  /** Optional chapter label. New screens put context in a lead line instead. */
  eyebrow?: string;
  title: ReactNode;
  count?: number | string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-lg flex flex-wrap items-end justify-between gap-md border-b-[0.5px] border-hairline pb-md">
      <div>
        {eyebrow && (
          <Eyebrow>
            § {eyebrow}
            {count != null && (
              <>
                {' · '}
                <span className="tnum">{count}</span>
              </>
            )}
          </Eyebrow>
        )}
        <h1 className={eyebrow ? 'mt-2xs text-headline' : 'text-headline'}>{title}</h1>
      </div>
      {actions && <div className="flex items-center gap-sm">{actions}</div>}
    </header>
  );
}
